/**
 * Comprovante de separação (Requisição de Separação de Ferragens) — papel pra
 * colar na caixa dos itens separados, com assinatura do almoxarife e data.
 * Mesmo espírito do comprovante das RQAs (Gestão de Solicitações): janela
 * isolada com layout de impressão próprio + print().
 *
 * LAYOUT ADAPTATIVO EM 1 PÁGINA: o comprovante SEMPRE cabe numa única folha
 * A4, independente da quantidade de itens — a densidade escala por faixas
 * (nº de colunas + tamanho de fonte + espaçamento). Cabeçalho, dados e a
 * linha de assinatura ficam fixos; só a grade de itens compacta.
 */

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Quantidade: sem casas quando inteiro (24), ate 3 quando fracionario (6,04). */
function fmtQt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function dataBr(iso) {
  if (!iso) return "___/___/______";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "___/___/______"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** Faixas de densidade: quanto mais itens, mais colunas e menor a fonte.

 * Calibradas pela altura util da coluna numa A4 (margem 10mm, cabecalho +
 * assinatura fixos ≈ 260px, sobra ≈ 785px de coluna a 96dpi). Nome e meta
 * ficam em LINHA UNICA com ellipsis — altura por item deterministica, entao
 * a capacidade de cada faixa e garantida (nao depende de nome longo quebrar).
 * Acima de ~210 itens a linha de meta e omitida (so nome + qt) — capacidade
 * sobe pra ~360, muito alem de qualquer lista real de ferragens.
 */
function densidade(n) {
  if (n <= 14) return { colunas: 1, fonte: 13, meta: 11, pad: 6, slip: "480px", comMeta: true };
  if (n <= 40) return { colunas: 2, fonte: 11.5, meta: 9.5, pad: 4, slip: "100%", comMeta: true };
  if (n <= 75) return { colunas: 3, fonte: 10, meta: 8.5, pad: 3, slip: "100%", comMeta: true };
  if (n <= 110) return { colunas: 4, fonte: 9, meta: 8, pad: 2.5, slip: "100%", comMeta: true };
  if (n <= 160) return { colunas: 5, fonte: 8, meta: 7.5, pad: 2, slip: "100%", comMeta: true };
  if (n <= 210) return { colunas: 6, fonte: 7.5, meta: 7, pad: 1.5, slip: "100%", comMeta: true };
  return { colunas: 6, fonte: 7.5, meta: 7, pad: 1.5, slip: "100%", comMeta: false };
}

function montarHtmlComprovante(req) {
  const itensLista = (req.lista?.itens || []).filter(
    (it) => !it.oculto && Number(it.quantidade) > 0,
  );
  const d = densidade(itensLista.length);

  const itens = itensLista
    .map((it, i) => {
      const meta = [it.ambiente_nome, it.fabricante, it.dimensoes]
        .filter(Boolean)
        .join(" · ");
      const metaHtml = d.comMeta && meta ? `<i title="${esc(meta)}">${esc(meta)}</i>` : "";
      return `
      <div class="item">
        <span class="n">${i + 1}</span>
        <span class="nm"><b title="${esc(it.nome)}">${esc(it.nome)}</b>${metaHtml}</span>
        <span class="qt">×${esc(fmtQt(it.quantidade))}</span>
      </div>`;
    })
    .join("");

  const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>Separação ${esc(req.numero)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; }
  .slip { width: 100%; max-width: ${d.slip}; margin: 0 auto; padding: 14px 14px 10px; }
  .marca { font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #5b5bf6; }
  h1 { font-size: 22px; margin: 2px 0 6px; letter-spacing: -0.5px; }
  .meta { font-size: 11px; line-height: 1.45; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; padding: 5px 0; margin-bottom: 6px; }
  .meta b { color: #555; font-weight: 600; }
  .contagem { font-size: 9.5px; color: #777; text-transform: uppercase; letter-spacing: .05em; margin: 0 0 5px; }
  .itens { column-count: ${d.colunas}; column-gap: 14px; column-rule: 1px solid #eee; }
  .item { display: flex; align-items: baseline; gap: 5px; padding: ${d.pad}px 0;
    border-bottom: 1px solid #eee; font-size: ${d.fonte}px; line-height: 1.2;
    break-inside: avoid; -webkit-column-break-inside: avoid; }
  .item .n { flex: 0 0 auto; min-width: 16px; color: #999; font-size: ${d.meta}px; }
  .item .nm { flex: 1; min-width: 0; }
  /* linha UNICA com ellipsis — altura por item deterministica (garante 1 pagina) */
  .item .nm b, .item .nm i { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .item .nm b { font-weight: 500; }
  .item .nm i { font-style: normal; color: #888; font-size: ${d.meta}px; font-weight: 400; }
  .item .qt { flex: 0 0 auto; font-weight: 700; white-space: nowrap; }
  .sep { margin-top: 10px; font-size: 11px; line-height: 1.5; }
  .sep b { color: #555; font-weight: 600; }
  .assinatura { margin-top: 22px; text-align: center; }
  .linha { border-top: 1px solid #333; width: 62%; margin: 0 auto 5px; }
  .assinatura span { font-size: 11px; color: #555; }
  @page { size: A4; margin: 10mm; }
  @media print {
    .slip { max-width: 100%; padding: 0; }
    /* 1 pagina SEMPRE: a grade compacta por faixas e nada quebra pra 2a folha */
    .assinatura { page-break-inside: avoid; }
  }
</style></head>
<body>
  <div class="slip">
    <div class="marca">PharuERP · Comprovante de separação</div>
    <h1>${esc(req.numero)}</h1>
    <div class="meta">
      <div><b>Cliente:</b> ${esc(req.cliente_nome || "—")}</div>
      <div><b>Projeto:</b> ${esc(req.projeto_resumo || "—")}</div>
      <div><b>Solicitante:</b> ${esc(req.criado_por_nome || "—")}</div>
      <div><b>Origem:</b> Revisor de Ferragens — ${esc(req.lista_compras_titulo || "lista de compras")}</div>
    </div>
    <div class="contagem">${itensLista.length} ${itensLista.length === 1 ? "item separado" : "itens separados"}</div>
    <div class="itens">${itens || '<div class="item">Sem itens.</div>'}</div>
    <div class="sep">
      <div><b>Separado por:</b> ${esc(req.separado_por_nome || "")}</div>
      <div><b>Data da separação:</b> ${dataBr(req.separado_em)}</div>
    </div>
    <div class="assinatura">
      <div class="linha"></div>
      <span>Assinatura do almoxarife</span>
    </div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},80);};</script>
</body></html>`;

  return html;
}

/**
 * Escreve o comprovante numa janela JÁ ABERTA. A janela deve ser aberta
 * SINCRONICAMENTE no clique (antes de qualquer await) — depois de uma cadeia
 * de requests o navegador bloqueia o pop-up (ativação do clique expira).
 */
export function escreverComprovanteSeparacaoFerragens(janela, req) {
  if (!janela || janela.closed) return false;
  const html = montarHtmlComprovante(req);
  janela.document.open();
  janela.document.write(html);
  janela.document.close();
  return true;
}

/** Abre e imprime na hora (uso síncrono no clique — ex.: Reimprimir). */
export function imprimirComprovanteSeparacaoFerragens(req) {
  const w = window.open("", "_blank", "width=900,height=720");
  return escreverComprovanteSeparacaoFerragens(w, req);
}
